import type { Rule } from 'antd/es/form';

const KEYBOARD_SEQS = [
  'qwertyuiop',
  'asdfghjkl',
  'zxcvbnm',
  '1234567890',
  'poiuytrewq',
  'lkjhgfdsa',
  'mnbvcxz',
  '0987654321',
];

const containsKeyboardSubstr = (
  pwd: string,
  seq: string,
  minLen: number,
): boolean => {
  for (let i = 0; i <= seq.length - minLen; i++) {
    const substr = seq.slice(i, i + minLen);
    if (pwd.includes(substr)) {
      return true;
    }
    for (let len = minLen + 1; i + len <= seq.length; len++) {
      const longerSubstr = seq.slice(i, i + len);
      if (pwd.includes(longerSubstr)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * 校验密码强度（与 Vue 端规则一致）。空值由 required 规则处理，此处直接通过。
 */
export const validatePasswordStrength = (
  value?: string,
): string | undefined => {
  if (!value) {
    return undefined;
  }

  if (value.length < 8) {
    return '密码至少需要8个字符';
  }

  const hasUpperCase = /[A-Z]/.test(value);
  const hasLowerCase = /[a-z]/.test(value);
  const hasNumbers = /\d/.test(value);
  const hasSpecialChar = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(value);

  const typeCount = [
    hasUpperCase,
    hasLowerCase,
    hasNumbers,
    hasSpecialChar,
  ].filter(Boolean).length;

  if (typeCount < 3) {
    return '密码必须包含大写字母、小写字母、数字、特殊符号中的至少3种';
  }

  if (/(.)\1{2,}/.test(value)) {
    return '密码不能包含连续3个相同字符';
  }

  const lowerValue = value.toLowerCase();
  for (const seq of KEYBOARD_SEQS) {
    if (containsKeyboardSubstr(lowerValue, seq, 3)) {
      return '密码不能包含键盘序列（如qwe、123等连续字符）';
    }
  }

  return undefined;
};

/** 校验密码（含必填），用于提交前等非表单场景 */
export const validatePassword = (
  value?: string,
  emptyMessage = '请输入密码',
): string | undefined => {
  if (!value) {
    return emptyMessage;
  }
  return validatePasswordStrength(value);
};

/** Ant Design 表单密码强度校验器 */
export const passwordStrengthValidator: Rule['validator'] = (_, value) => {
  const error = validatePasswordStrength(value);
  if (error) {
    return Promise.reject(new Error(error));
  }
  return Promise.resolve();
};

export type PasswordRulesOptions = {
  required?: boolean;
  requiredMessage?: string;
};

/** 新密码/密码字段通用 rules */
export const createPasswordRules = (
  options: PasswordRulesOptions = {},
): Rule[] => {
  const { required = true, requiredMessage = '请输入密码' } = options;
  const rules: Rule[] = [];

  if (required) {
    rules.push({ required: true, message: requiredMessage });
  }

  rules.push({ validator: passwordStrengthValidator });

  return rules;
};

export type ConfirmPasswordRulesOptions = {
  matchField: string;
  requiredMessage?: string;
  mismatchMessage?: string;
};

/** 确认密码字段通用 rules */
export const createConfirmPasswordRules = (
  options: ConfirmPasswordRulesOptions,
): Rule[] => {
  const {
    matchField,
    requiredMessage = '请确认密码',
    mismatchMessage = '两次输入的密码不一致',
  } = options;

  return [
    { required: true, message: requiredMessage },
    ({ getFieldValue }) => ({
      validator(_, value) {
        if (!value || getFieldValue(matchField) === value) {
          return Promise.resolve();
        }
        return Promise.reject(new Error(mismatchMessage));
      },
    }),
  ];
};
